import { useEffect, useMemo, useRef, useState } from 'react'
import { activeEntry, canCall, lineage, useStore } from '../core/store'
import { PROVIDERS } from '../core/models'
import { branchMarkdown, markdownFilename } from '../core/markdown'
import { ago } from './components/ago'
import { Icon, type IconName } from './components/Icon'
import { useModalFocus } from './components/useModalFocus'
import { useMedia } from './components/useMedia'
import { kindLabelOf } from '../core/types'
import { CHANNEL_KEYS } from '../core/visibility'
import { upNextShut } from '../core/upNext'
import { planProgramme, scanForBridges } from '../engine/reason'
import { loadDemo } from '../engine/demo'
import { listRecent, type RecentEntry } from '../core/persist'
import { canForgetEverything } from '../core/forget'
import { SOURCES_DOOR } from '../core/corpus'
import { askForgetAll } from './forgetPress'
import { openFromIndex } from './ExplorationSearch'
import { PALETTE_KEYS, paletteExplorations, rankPalette } from './paletteSearch'
import { paletteVerbRows, pressPaletteVerb } from './paletteVerbs'
import { focusRunPress } from './RunStrip'
import { readFirstOf } from './SharedCopyBanner'

interface Item {
  id: string
  group: string
  title: string
  sub?: string
  /** The `sub` in full, for a row whose visible line is an abbreviation of it. A
   *  node row prints the first 78 characters of a claim and holds all of it, so
   *  without this a word past that point is unreachable — the query would be
   *  scored against the ellipsis. */
  hay?: string
  /** Words this row answers to and never prints — see `paletteSearch.ts`. */
  keys?: string
  icon: IconName
  run: () => void
}

export function CommandPalette({ stageW, stageH }: { stageW: number; stageH: number }) {
  const open = useStore((s) => s.paletteOpen)
  const setOpen = useStore((s) => s.setPalette)
  const session = useStore((s) => s.session)
  const selectedId = useStore((s) => s.selectedId)
  const past = useStore((s) => s.past)
  const future = useStore((s) => s.future)
  const busy = useStore((s) => s.busy)
  /** Where the replay stands, for the branch row: scrubbed to a step, it offers
   *  to branch there. */
  const playhead = useStore((s) => s.playhead)
  // Which verb rows the line can take, per verb — see `paletteVerbs.ts`. Read
  // as booleans so the list is rebuilt exactly when an answer changes. The
  // conjecture line's three wait as the checks do (`upNextWaits`), so the check's
  // answer is theirs too.
  const movesShut = useStore((s) => upNextShut(s, 'deepen'))
  const checksShut = useStore((s) => upNextShut(s, 'verify'))
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  const narrow = useMedia('(max-width: 767px)')

  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  // Inert background, Tab containment and focus return. The hook also focuses the
  // dialog on open, but the search input is the point here, so the line below
  // re-takes it the next frame — a later focus wins.
  useModalFocus(dialogRef, open)

  useEffect(() => {
    if (open) {
      setQ('')
      setActive(0)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Only when the palette is open: the memos below also re-run on every session
  // mutation, and during a stream that is once per arriving node — no reason to
  // parse the index each time for a list nobody is looking at. Read once here
  // for every row that needs it, and for the query's exploration rows (D335).
  const stored = useMemo(() => (open ? listRecent() : []), [open, session])

  const items = useMemo<Item[]>(() => {
    const st = useStore.getState()
    const out: Item[] = []
    const sel = selectedId ? session.nodes[selectedId] : undefined

    if (sel) {
      const group = `On “${sel.label}”`
      // The six moves, the two checks, the seed's survey and the conjecture
      // line's three — each the key's own press, so under a pass in flight it
      // joins the Up next line behind it or says why not (`paletteVerbs.ts`),
      // where these rows used to vanish.
      for (const row of paletteVerbRows(st, sel)) {
        out.push({
          id: row.id,
          group,
          title: row.title,
          sub: row.sub,
          icon: row.icon,
          run: () => void pressPaletteVerb(row, sel.id),
        })
      }
      // Exporting mid-stream is legitimate, so this one is never gated on the
      // lock.
      if (sel.parentId !== null) {
        out.push({
          id: 'cmd-copy-branch',
          group,
          title: 'Copy this branch as Markdown',
          sub: `Seed → ${sel.label} · ${lineage(session, sel.id).length} steps, with evidence and next steps`,
          icon: 'copy',
          run: () => void copyBranch(sel.id),
        })
      }
    }

    out.push({
      id: 'cmd-bridge',
      group: 'Explore',
      // The Overview's press, in its words: one thing, one name (D155).
      title: 'Find connections',
      sub: 'Scan for two branches that fuse',
      icon: 'bridge',
      run: () => void scanForBridges(),
    })
    // Absent on a canvas with no graph, rather than latching: the tab this poke
    // opens cannot mount without one, and an ask with no surface to consume it
    // would fire later, on a graph the press never referred to.
    if (session.order.length > 0) {
      out.push({
        id: 'cmd-bridge-across',
        group: 'Explore',
        title: 'Find connections across explorations',
        sub: 'Open the pick — choose which stored graphs the scan may read. Spends nothing yet',
        icon: 'bridge',
        // A poke, not a pass. This row used to run the default read directly,
        // and the first person to meet it pressed it expecting a choice and
        // bought a reading instead — a row that cannot show the pick must not
        // be the door that spends past it.
        run: () => st.pokeScanAcross(),
      })
    }
    out.push(
      {
        id: 'cmd-research-lab',
        group: 'Explore',
        title: 'Research Lab',
        sub: 'Set the evidence protocol, inspect genealogy, test contradictions, version the argument, compare models, and audit the trail',
        icon: 'experiment',
        run: () => st.setResearchOpen(true),
      },
      {
        id: 'cmd-sources',
        group: 'Explore',
        // The door's one name and the sidebar's glyph for it (D390): the glass
        // is search's, and the sidebar draws this row as a page.
        title: SOURCES_DOOR,
        sub: 'Paste documents a source check should search and cite',
        icon: 'file',
        run: () => st.setSources(true),
      },
      {
        id: 'cmd-fit',
        group: 'View',
        title: 'Fit the whole graph',
        icon: 'fit',
        run: () => {
          st.setFocus(null)
          st.frame(stageW, stageH)
        },
      },
      {
        id: 'cmd-view',
        group: 'View',
        icon: 'cube' as IconName,
        title: st.viewMode === '3d' ? 'Back to the flat view' : 'Spatial view',
        sub:
          st.viewMode === '3d'
            ? 'The radial canvas, where thickness is confidence again'
            : 'Height is how much a claim has been checked; bridges arc over the graph',
        run: () => {
          const r = document.querySelector('.stage')?.getBoundingClientRect()
          st.setViewMode(st.viewMode === '3d' ? '2d' : '3d', r?.width, r?.height)
        },
      },
      {
        id: 'cmd-theme',
        group: 'View',
        title: st.theme === 'dark' ? 'Switch to light appearance' : 'Switch to dark appearance',
        // Static, naming BOTH mediums: the flipping title only contains the
        // theme you would switch TO, so searching "dark" while already dark
        // found nothing. The sub keeps either query matching — and says what
        // the two appearances actually are.
        sub: 'The dark night field, or the light printed plate',
        icon: st.theme === 'dark' ? 'sun' : 'moon',
        run: () => st.setTheme(st.theme === 'dark' ? 'light' : 'dark'),
      },
      {
        id: 'cmd-focus',
        group: 'View',
        title: st.focusId ? 'Release focus' : 'Focus the selected branch',
        icon: 'target',
        run: () => st.setFocus(st.focusId ? null : selectedId),
      },
      // Both gated, so neither is ever a permanently inert row.
      ...(sel &&
      sel.parentId !== null &&
      session.order.some((i) => session.nodes[i]?.parentId === sel.id)
        ? [
            {
              id: 'cmd-fold',
              group: 'View',
              icon: 'chevron' as IconName,
              title: sel.collapsed ? 'Unfold this branch' : 'Fold this branch away',
              sub: '⌥-click a node, or H',
              run: () => st.toggleCollapse(sel.id),
            },
          ]
        : []),
      ...(session.order.some((i) => session.nodes[i]?.collapsed)
        ? [
            {
              id: 'cmd-unfold-all',
              group: 'View',
              icon: 'expand' as IconName,
              title: 'Unfold every branch',
              run: () => st.expandAll(),
            },
          ]
        : []),
      {
        // One row that flips, rather than one per weighting: the title says
        // which way it would go, so the palette never offers the state the
        // canvas is already in.
        id: 'cmd-weighting',
        group: 'View',
        icon: 'info' as IconName,
        title:
          st.weighting === 'evidence'
            ? 'Size claims by the model’s own ratings'
            : 'Size claims by what has been established',
        sub:
          st.weighting === 'evidence'
            ? 'Back to novelty and confidence as the model rated them'
            : 'Thickness and size from source checks, other providers, panels and recorded outcomes; the halo only from measured novelty',
        run: () => st.setWeighting(st.weighting === 'evidence' ? 'model' : 'evidence'),
      },
      // Where the strip's press is: a key, a graph, no pass or run in flight, and a
      // composer on screen to hold it (a shared copy read first has none).
      ...(canCall(st) && !runLive && !busy && session.order.length > 0 && !readFirstOf(st)
        ? [
            {
              id: 'cmd-run',
              group: 'Explore',
              icon: 'play' as IconName,
              title: 'Let it run',
              sub: 'Takes you to the run’s press above the input, with its moves and estimate beside it. Nothing starts until you press it.',
              // This row used to start its own run: always 24 passes, none of the
              // checks or panels set under Adjust, and no figure — a different,
              // unpriced run under the strip's own name (D338). Now it starts
              // nothing. It closes the palette and seats focus on the strip's
              // press, where the dials' moves and the estimate stand beside it,
              // so one ↵ more buys exactly the run that was priced — or the rest
              // of the last one, where Continue has taken the press. The old
              // row's refusal to buy panels or a cross-examination from here
              // without saying so holds by construction: the strip prices them.
              //
              // A phone's open sheet is where the composer has yielded, so it
              // closes first; the strip's row takes the ask once it is back.
              run: () => {
                if (narrow && st.railRight) st.setRail(false)
                focusRunPress()
              },
            },
          ]
        : []),
      ...(canCall(st) && !st.run
        ? [
            {
              // One pass, and it lands nothing: the list it proposes waits on
              // the sheet for an approval, and that press is the only thing
              // that spends the budget the list implies.
              id: 'cmd-programme',
              group: 'Explore',
              icon: 'play' as IconName,
              title: 'Propose a programme',
              sub: 'One reasoning pass proposes up to twelve moves for you to approve as one batch. Nothing runs until you approve it.',
              run: () => void planProgramme(),
            },
          ]
        : []),
      {
        // ONE row, not one per model. A row that switched model directly would be a
        // second selection path that skips the key check — and "reason with Terra"
        // from here would silently do nothing on a workspace with no OpenAI key.
        id: 'cmd-model',
        group: 'Session',
        title: canCall(st) ? `Model: ${activeEntry(st).name}` : 'Choose a model and connect a key',
        sub: canCall(st)
          ? `${PROVIDERS[activeEntry(st).provider].name} · change the model or replace the key`
          : "Nothing runs until the selected model's provider has a key",
        icon: 'key',
        run: () => st.setSetup('settings'),
      },
      {
        // The sidebar's "New exploration" as a searchable row — "new"
        // previously matched nothing but a node sub ("micronewton"). One shared
        // door with the sidebar and ⌘N: guard, toast and clear live in
        // `newExploration`, so the three cannot drift apart.
        id: 'cmd-new',
        group: 'Session',
        title: 'New exploration',
        sub: 'A blank canvas — the current exploration stays saved on this device. ⌘N',
        icon: 'plus',
        run: () => void useStore.getState().newExploration(),
      },
      {
        id: 'cmd-demo',
        group: 'Session',
        title: 'Load the demo exploration',
        sub: 'Antigravity, fully reasoned',
        icon: 'play',
        run: () => void loadDemo(),
      },
      // The rail's Method and Customize rows died with the rail. This is the
      // first-run sheet's only way back in once it has been dismissed, so it gets
      // a named, searchable row here rather than vanishing with the chrome. Named
      // for what the sheet now IS (D335): it was "Method — how Pathways takes a
      // claim apart", and the explanation moved to docs/guide.md long ago, so
      // the row promised an essay and opened a key form. The id stays.
      {
        id: 'cmd-method',
        group: 'Session',
        title: 'Get started',
        sub: 'The recommended model, its key, and the worked example',
        icon: 'experiment',
        run: () => st.setSetup('splash'),
      },
      // Beside Get started, because these two are the only Session rows that
      // explain the app rather than change it. The subtitle ends in the key the sheet
      // answers to, the way the New exploration row ends in ⌘N — a reference the
      // user found once through the palette should be reachable without it after.
      {
        id: 'cmd-shortcuts',
        group: 'Session',
        title: 'Keyboard shortcuts',
        sub: 'Every key this app binds, and where each one works. ?',
        icon: 'command',
        run: () => st.setShortcuts(true),
      },
      // The whole prompt, for whoever cannot press the seed caption's "Show
      // more": the visual captions are pointer affordances, out of the tab order
      // and the accessibility tree, so the sheet needs a door a keyboard can
      // reach. Only once there is a seed to read.
      ...(session.order.length > 0
        ? [
            {
              id: 'cmd-prompt',
              group: 'Session',
              title: 'Read the whole prompt',
              sub: 'The seed of this exploration, exactly as it was typed',
              icon: 'pathways' as IconName,
              run: () => st.setPromptOpen(true),
            },
          ]
        : []),
      {
        id: 'cmd-tune',
        group: 'Session',
        title: 'Customize the next run',
        // It named the panel as where "Let it run" lives, false since the run
        // moved to the strip (D179, D338).
        sub: 'Lens, temper and the run budget for the next run — Run itself is on the row above the input',
        icon: 'spark',
        run: () => st.setTune(true),
      },
      // Replay lives in the transport bar, on an icon a first-time viewer can miss.
      // Surfacing it here makes "watch it grow again" a named, searchable command.
      // Shown only when there is a graph worth scrubbing — under four nodes the
      // transport shows no scrubber either.
      ...(session.order.length >= 4
        ? [
            {
              id: 'cmd-replay',
              group: 'Session',
              title: 'Replay how the graph grew',
              sub: 'Watch it unfurl in arrival order — often more telling than the finished shape',
              icon: 'play' as IconName,
              run: () => {
                const s = useStore.getState()
                // Mirror the transport's own guard: replaying a graph that is still
                // growing can never terminate, because the playhead chases a length
                // that keeps increasing.
                if (s.run && s.run.phase !== 'ended') {
                  s.toast(
                    'Replay is off while a run is going — the graph is still growing.',
                    'warn',
                  )
                  return
                }
                s.setFocus(null)
                s.setPlayhead(1)
                s.setPlaying(true)
              },
            },
          ]
        : []),
      // Rewind now means undo; three meanings of one glyph in a scrolling list
      // is one too many.
      // Gated on `busy` for the same reason the keystroke is: stepping the graph
      // back mid-stream would race the drain that is still threading nodes on.
      // And on a live run, matching the door the store now holds — the row for
      // a press the store would refuse is chrome about nothing.
      ...(past.length && !busy && !runLive
        ? [
            {
              id: 'cmd-undo',
              group: 'Session',
              title: `Undo ${past[past.length - 1]!.label}`,
              sub: 'Step the graph back',
              icon: 'rewind' as IconName,
              run: () => st.undo(),
            },
          ]
        : []),
      ...(future.length && !busy && !runLive
        ? [
            {
              id: 'cmd-redo',
              group: 'Session',
              title: `Redo ${future[future.length - 1]!.label}`,
              sub: 'The same nodes, not a new call',
              icon: 'forward' as IconName,
              run: () => st.redo(),
            },
          ]
        : []),
      // The two ways to take this exploration somewhere else, as searchable rows:
      // "fork" found nothing while both doors existed — one behind a row's ⋮, the
      // other on a step card only a pointer can raise. Each row is that door's
      // own act and its own sentence, nothing new; both are held by the lock the
      // doors hold, so a row never offers what the store would refuse.
      ...(session.order.length > 0 && !busy && !runLive
        ? [
            {
              id: 'cmd-fork',
              group: 'Session',
              title: 'Fork this exploration',
              sub: 'A copy of the whole graph to try something on; this one stays as it is',
              icon: 'fork' as IconName,
              run: () => {
                if (!st.duplicateSession()) return
                // Read back off the copy, as the sidebar's fork does: the store's
                // copy IS the session now, and its family is the one it filed.
                const label = useStore.getState().session.project
                useStore.getState().toast(label ? `Forked into “${label}”.` : 'Forked.', 'good')
              },
            },
          ]
        : []),
      // A name for the open exploration's row. The palette has no field, and a
      // second place to type a name would be a second set of rules for it, so
      // the row asks the sidebar to open the one field there is (`sigNaming`).
      // Offered once the exploration has a row to name — it is listed once it
      // grows past its seed — and never locked: a name spends nothing and
      // touches nothing a pass reads.
      ...(stored.some((r) => r.id === session.id)
        ? [
            {
              id: 'cmd-rename',
              group: 'Session',
              title: 'Rename this exploration',
              sub: 'The name its row, the library and the tab show; the seed stays as typed',
              icon: 'edit' as IconName,
              run: () => st.pokeNaming(),
            },
          ]
        : []),
      ...(session.order.length > 1 && !busy && !runLive
        ? [
            playhead !== null && playhead >= 1 && playhead <= session.order.length
              ? {
                  id: 'cmd-branch',
                  group: 'Session',
                  title: `Branch from step ${playhead}`,
                  sub: `A new exploration carrying the first ${playhead} step${playhead === 1 ? '' : 's'} of the replay; this one stays as it is`,
                  icon: 'fork' as IconName,
                  // The step card's own press, at the step the replay stands on.
                  run: () => {
                    const now = useStore.getState()
                    if (now.busy || (now.run !== null && now.run.phase !== 'ended')) {
                      now.toast('One thought at a time — let the current branch finish.', 'warn')
                      return
                    }
                    if (st.forkAtStep(playhead - 1)) {
                      st.toast(
                        `Branched at step ${playhead} — ${playhead} node${playhead === 1 ? '' : 's'} carried.`,
                        'good',
                      )
                    }
                  },
                }
              : {
                  id: 'cmd-branch',
                  group: 'Session',
                  title: 'Branch from a step…',
                  sub: 'Stands the replay one step back — pick a step on the rail, then branch from its card or from here',
                  icon: 'fork' as IconName,
                  // The door to the choice, not a choice made for you: the rail's
                  // ticks seek, its card branches, and ⌘K offers the step the
                  // replay stands on. Paused, so nothing moves under the pick.
                  run: () => {
                    st.setPlaying(false)
                    st.setFocus(null)
                    st.setPlayhead(Math.max(1, session.order.length - 1))
                  },
                },
          ]
        : []),
      // The search sheet's door, beside the library's. The sheet has no chord of
      // its own, and without a row here the palette and the sheet each searched
      // half the app behind the same magnifying glass (D335).
      ...(stored.length
        ? [
            {
              id: 'cmd-search-explorations',
              group: 'Session',
              title: 'Search explorations',
              sub: 'Every stored exploration, by what it is about, its name or its project',
              icon: 'search' as IconName,
              run: () => st.setSearchOpen(true),
            },
          ]
        : []),
      {
        id: 'cmd-library',
        group: 'Session',
        // The sidebar's name for the sheet, and the sheet's own — one thing, one
        // name. It was "Explorations" here and on the sheet while the sidebar's
        // door said Library, so the door and the room it opened disagreed.
        title: 'Library',
        sub: 'Everything saved on this device, and what saving means here',
        icon: 'library',
        run: () => st.setLibrary(true),
      },
      // One row where there were two. The seven file formats differ in who they are for
      // rather than in encoding, and a palette row cannot carry the sentence that
      // decides between them — so it opens the sheet that can.
      {
        id: 'cmd-export-md',
        group: 'Session',
        title: 'Share or export this exploration',
        sub: 'A self-contained link, research report, visual PDF, model briefing, full record, JSON, run manifest, or portable audit capsule',
        icon: 'download',
        run: () => st.setExport(true),
      },
      // Inside the Session block, not after the Recent rows — the list renders a
      // header whenever the group changes, so a second Session row further down
      // would print the header twice.
      ...(stored.length && canForgetEverything()
        ? [
            {
              id: 'cmd-forget',
              group: 'Session',
              title: 'Forget saved explorations…',
              sub: 'Opens the Library to confirm — clears every stored graph on this device',
              icon: 'trash' as IconName,
              // A door, never the deed (D335). This row forgot everything on one
              // Enter, and "for" on an empty canvas ranked it first and alone —
              // a reader reaching for Fork or Focus by prefix wiped the library.
              // It opens the library's own confirm, armed: the count, the
              // recorded-results clause and the pending state come from the one
              // component that already says them (D331's path, unchanged).
              run: () => {
                askForgetAll()
                st.setLibrary(true)
              },
            },
          ]
        : []),
    )

    // The four newest, for an empty query. A typed one is answered from the
    // whole index instead — see `filtered`.
    for (const e of paletteExplorations(stored, session.id, '')) out.push(storedRow(e, 'Recent'))
    const toggles: Array<[string, keyof typeof st.filters]> = [
      ['contradictions', 'contradictions'],
      ['knowledge gaps', 'gaps'],
      ['weak inferences', 'weak'],
      ['closed paths', 'dead'],
      ['signal flow', 'particles'],
      ['labels', 'labels'],
    ]
    // One door back from any number of colour switches turned off, as the
    // legend's Show all is, and named for what it restores — offered only while
    // one is off (D335).
    if (CHANNEL_KEYS.some((k) => !st.filters[k]))
      out.push({
        id: 't-all',
        group: 'Show',
        title: 'Show all colors',
        icon: 'check',
        run: () => st.showAllChannels(),
      })
    for (const [name, key] of toggles) {
      out.push({
        id: `t-${key}`,
        group: 'Show',
        title: `${st.filters[key] ? 'Hide' : 'Show'} ${name}`,
        icon: 'check',
        run: () => st.toggleFilter(key),
      })
    }

    // Nodes come last so commands win an empty query, but a typed query
    // surfaces them by relevance alongside everything else.
    for (const id of session.order) {
      const n = session.nodes[id]
      if (!n || n.parentId === null) continue
      out.push({
        id: `n-${id}`,
        group: 'Nodes',
        title: n.label,
        sub: `${kindLabelOf(n)} · ${n.claim.slice(0, 78)}${n.claim.length > 78 ? '…' : ''}`,
        hay: `${kindLabelOf(n)} ${n.claim}`,
        icon: 'pathways',
        run: () => {
          // The list has no visibility filter, so it can name a node inside a
          // fold — and flying the camera to an invisible node is worse than
          // not offering it.
          st.revealNode(id)
          st.select(id)
          st.setRail(true)
          st.centerOn(id, stageW, stageH, Math.max(st.view.k, 0.75))
        },
      })
    }

    // The hidden words, attached by id from the one table the ranking is tested
    // against — see `paletteSearch.ts`.
    return out.map((it) => (PALETTE_KEYS[it.id] ? { ...it, keys: PALETTE_KEYS[it.id] } : it))
  }, [
    session,
    stored,
    selectedId,
    busy,
    movesShut,
    checksShut,
    stageW,
    stageH,
    open,
    past,
    future,
    playhead,
    runLive,
    narrow,
  ])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) {
      // Two budgets, not one. Commands are pushed ahead of nodes, so a single
      // slice would mean every command added later silently eats a node row —
      // and the node list is the only way to reach a node by name.
      const cmds = items.filter((it) => it.group !== 'Nodes')
      const nodes = items.filter((it) => it.group === 'Nodes')
      return [...cmds.slice(0, 30), ...nodes.slice(0, Math.max(14, 40 - cmds.length))]
    }
    // A row named by the word first, then a title containing it, then the rest.
    // The four Recent rows give way to every stored exploration the search sheet
    // would find, after the commands and before the nodes (D335) — so a name
    // typed here finds last week's exploration rather than offering to start it
    // again from the empty state.
    const ranked = rankPalette(
      items.filter((it) => it.group !== 'Recent'),
      needle,
    )
    const found = paletteExplorations(stored, session.id, needle).map((e) =>
      storedRow(e, 'Explorations'),
    )
    return [
      ...ranked.filter((it) => it.group !== 'Nodes'),
      ...found,
      ...ranked.filter((it) => it.group === 'Nodes'),
    ].slice(0, 40)
  }, [items, q, stored, session.id])

  useEffect(() => setActive(0), [q])

  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  if (!open) return null

  const choose = (i: number) => {
    const it = filtered[i]
    if (!it) return
    setOpen(false)
    it.run()
  }

  let lastGroup = ''

  // The query, quoted back once by the empty state and clipped there: a pasted
  // paragraph would otherwise fill the list it just failed to match.
  const typed = q.trim()
  const quoted = typed.length > 60 ? `${typed.slice(0, 59)}…` : typed

  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false)
      }}
    >
      <div
        className="palette panel"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        ref={dialogRef}
        tabIndex={-1}
      >
        <input
          ref={inputRef}
          className="palette-input"
          // Named, not merely prompted. A placeholder is only the fallback
          // accessible name, and it is gone the moment the field has text — so
          // the one control in this dialog lost its name at the exact point a
          // user had something typed and wanted to hear what it was searching.
          aria-label="Search nodes and commands"
          placeholder="Search nodes, or run a command…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false)
            else if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActive((a) => Math.min(a + 1, filtered.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive((a) => Math.max(a - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              choose(active)
            }
          }}
        />
        <div className="palette-list scroll" ref={listRef}>
          {filtered.length === 0 && (
            <div className="empty-note">
              <p style={{ marginBottom: 'var(--s3)' }}>
                Nothing here matches “{quoted}” — not a node, and not a command.
              </p>
              <button
                className="btn btn--outline"
                onClick={() => {
                  const s = useStore.getState()
                  setOpen(false)
                  // Into the idea box, not into a run: stating a claim is a
                  // decision, and the box is where the /commands and the price
                  // live. On a phone the thread sheet owns the bottom and
                  // hides the box, so the words would land out of sight — the
                  // sheet goes first.
                  if (narrow) s.setRail(false)
                  s.proposeSeed(typed)
                }}
              >
                State it as a new claim
              </button>
            </div>
          )}
          {filtered.map((it, i) => {
            const showGroup = it.group !== lastGroup
            lastGroup = it.group
            return (
              <div key={it.id}>
                {showGroup && <div className="palette-group eyebrow">{it.group}</div>}
                <button
                  className="palette-item"
                  data-active={i === active}
                  onPointerEnter={() => setActive(i)}
                  onClick={() => choose(i)}
                >
                  <Icon name={it.icon} size={13} />
                  <span className="palette-item-main">
                    <b>{it.title}</b>
                    {it.sub && <span>{it.sub}</span>}
                  </span>
                  {i === active && <span className="kbd">↵</span>}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * A stored exploration as a palette row — the four Recent ones, and what a query
 * finds. Opened through the search sheet's guard (`openFromIndex`), so a run that
 * is going refuses here as it does at every other door that lists explorations.
 */
function storedRow(e: RecentEntry, group: string): Item {
  return {
    id: `r-${e.id}`,
    group,
    // The name the sidebar prints, when somebody gave one — one thing, one name.
    title: e.name || e.seed || 'Untitled exploration',
    // The stored count is the insight CHANNEL — insights, takeaways and
    // syntheses alike (`summarise`, `kindChannelOf`) — and since D333 a bare
    // "insights" here would read as the insight cards alone, the word this
    // palette's own rows use for them. So it names all three, as the legend's
    // row and the tally's hover do.
    sub:
      `${e.nodes} node${e.nodes === 1 ? '' : 's'} · ${ago(e.savedAt)}` +
      (e.insights
        ? ` · ${e.insights} ${e.insights === 1 ? 'insight, takeaway or synthesis' : 'insights, takeaways or syntheses'}`
        : '') +
      (e.demo ? ' · demo' : ''),
    icon: 'pathways',
    run: () => void openFromIndex(e.id),
  }
}

function saveText(name: string, text: string, mime: string) {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * A branch goes to the clipboard, not to a file.
 *
 * It is a few kB and exists to be pasted; the whole exploration is tens of kB
 * and is a file you keep. The command titles carry the difference.
 */
async function copyBranch(id: string) {
  const st = useStore.getState()
  const n = st.session.nodes[id]
  if (!n) return
  const md = branchMarkdown(st.session, id)
  const steps = lineage(st.session, id).length
  try {
    await navigator.clipboard.writeText(md)
    st.toast(`Copied — ${steps} steps, up to “${n.label}”.`, 'good')
  } catch {
    saveText(markdownFilename(st.session, n), md, 'text/markdown')
    st.toast('Clipboard blocked — downloaded the file instead.', 'warn')
  }
}
